/**
 * Copyright 2023-2024 Highway9 Networks Inc.
 */
import { PayloadAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState } from "@reduxjs/toolkit";
import { RootState, store } from "..";
import { authenticationService, edgeService } from "~/services";
import { Edge, EdgeWithPool } from "~/types/edge";
import { EdgePool } from "~/types/EdgePool";
import { fillTimeSeriesData } from "~/views/subscribers/graphs/graphHelper";
import { MetricsQuery } from "~/services/APIServices";
import { observeStoreChange } from "../utils";
import { selectCompareEnable } from "./utilitySlice";
import { MetricsClient } from "~/sw/metrics/metricClient";
import { normalizeTime } from "~/helpers/utils";
import { EdgeUpgrade } from "~/types/AvailableUpgrades";
import { createSocketPatchReducer } from "../events/socketPatch";
import { Tunnel } from "~/types/Tunnel";
import { isAiRanEdge } from "~/types/AIRan";

export const edgeMetricWorker = new MetricsClient();
export const edgeMetricCompareWorker = new MetricsClient();

const edgesAdapter = createEntityAdapter<Edge>();
const edgePoolsAdapter = createEntityAdapter<EdgePool>();
const edgeUpgradesAdapter = createEntityAdapter<EdgeUpgrade>();
const tunnelsAdapter = createEntityAdapter<Tunnel>({
  selectId: (tunnel) => tunnel.id ?? `${tunnel.name}:${tunnel.port}:${tunnel.ip}`,
});

type MetricsMap = Record<string, [number, number][]>;

interface EdgeState {
  selectedId: string | null;
  selectedPoolId: string | null;
  edgesData: EntityState<Edge>;
  edgePoolsData: EntityState<EdgePool>;
  upgradesData: EntityState<EdgeUpgrade>;
  loading: boolean;
  edgePoolsLoading: boolean;

  support: {
    dialog: boolean;
    type: "enable" | "disable" | null;
    id: string | null;
  };

  kpiOptions: {
    plmn: string;
    technology: string;
    templateId: string;
  };

  pcap: { operationID: string | null };
  techBundle: { operationID: string | null };

  metricsLoading: boolean;
  metricsError: string | null;
  metrics: MetricsMap;
  compareMetrics: MetricsMap;
  aggregatedMetrics: MetricsMap;

  tunnels: {
    loading: boolean;
    tunnelsData: EntityState<Tunnel>;
    selectedTunnelId: string | null;
    open: boolean;
    isEditing: boolean;
  };

  hasPartnerRadioManager: boolean;
}

export const initialState: EdgeState = {
  selectedId: null,
  selectedPoolId: null,
  loading: true,
  edgePoolsLoading: true,
  edgesData: edgesAdapter.getInitialState(),
  edgePoolsData: edgePoolsAdapter.getInitialState(),
  upgradesData: edgeUpgradesAdapter.getInitialState(),

  support: {
    dialog: false,
    id: null,
    type: null,
  },

  kpiOptions: {
    plmn: "",
    technology: "4G",
    templateId: "",
  },
  pcap: { operationID: null },
  techBundle: { operationID: null },

  metricsLoading: true,
  metricsError: null,
  metrics: {},
  compareMetrics: {},
  aggregatedMetrics: {},

  tunnels: {
    loading: false,
    tunnelsData: tunnelsAdapter.getInitialState(),
    selectedTunnelId: null,
    open: false,
    isEditing: false,
  },

  hasPartnerRadioManager: false,
};

function clearStaleSelection(state: EdgeState) {
  if (state.selectedId && !state.edgesData.entities[state.selectedId]) {
    state.selectedId = null;
  }
}

export type EdgeMetricsQuery = MetricsQuery & {
  /** When true, writes into compareMetrics via the compare worker. */
  compare?: boolean;
};

export const fetchEdgeMetrics = createAsyncThunk(
  "edge/fetchEdgeMetrics",
  async (query: EdgeMetricsQuery, thunkApi) => {
    try {
      const { compare, ...metricsQuery } = query;
      const state = thunkApi.getState() as RootState;
      const { interval, timeZone } = state.utility.time;
      const worker = compare ? edgeMetricCompareWorker : edgeMetricWorker;

      const normalizedQuery: MetricsQuery = {
        ...metricsQuery,
        interval: {
          startTime: normalizeTime(metricsQuery.interval.startTime, metricsQuery.resolution, timeZone),
          endTime: normalizeTime(metricsQuery.interval.endTime, metricsQuery.resolution, timeZone),
        },
      };

      let metrics: MetricsMap = {};
      await worker.initialize(
        "edges",
        normalizedQuery,
        authenticationService.currentUserValue!.token,
        (_id, data) => {
          metrics = data as MetricsMap;
        },
        { noPolling: interval === "custom", interval: 30 },
      );
      return { metrics, compare: !!compare };
    } catch (err: any) {
      return thunkApi.rejectWithValue(err.errors[0].message);
    }
  },
);

export const fetchEdgeMetricsAggregate = createAsyncThunk(
  "edge/fetchEdgeMetricsAggregate",
  edgeService.getAggregateMetrics,
);

export const fetchEdges = createAsyncThunk("edge/fetchEdges", async (query?: { timestamp?: number }) => {
  return edgeService.getEdges(query?.timestamp ? { timestamp: query.timestamp } : {});
});

export const fetchEdgePools = createAsyncThunk(
  "edge/fetchEdgePools",
  async (query?: { timestamp?: number }) => {
    return edgeService.getEdgePool(query?.timestamp ? { timestamp: query.timestamp } : {});
  },
);

export const fetchEdgeUpgrades = createAsyncThunk("edge/fetchEdgeUpgrades", () =>
  edgeService.getEdgeUpgrades(),
);

export const fetchTunnels = createAsyncThunk("edge/fetchTunnels", (edgeId: string) =>
  edgeService.getTunnels(edgeId),
);

export const fetchHasPartnerRadioManager = createAsyncThunk(
  "edge/fetchHasPartnerRadioManager",
  async () => {
    const res = await edgeService.getPartnerRadioManagers();
    return (res.data?.length ?? 0) > 0;
  },
);

export const edgeSlice = createSlice({
  name: "edge",
  initialState,
  reducers: {
    /** Store selection identity only; resolve via selectors. */
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setSelectedPool(state, action: PayloadAction<string | null>) {
      state.selectedPoolId = action.payload;
    },

    setSupportDialogOpen: (
      state,
      action: PayloadAction<{ open: boolean; type: "enable" | "disable" | null }>,
    ) => {
      state.support.dialog = action.payload.open;
      state.support.type = action.payload.type;
    },
    setSupportId: (state, action: PayloadAction<string | null>) => {
      state.support.id = action.payload;
    },

    setKPIPlmn(state, action: PayloadAction<string>) {
      state.kpiOptions.plmn = action.payload;
    },
    setKPITechnology(state, action: PayloadAction<string>) {
      state.kpiOptions.technology = action.payload;
    },
    setKPITemplateId(state, action: PayloadAction<string>) {
      state.kpiOptions.templateId = action.payload;
    },
    clearCompareMetrics(state) {
      state.compareMetrics = {};
    },

    setPcapOperationID(state, action: PayloadAction<string | null>) {
      state.pcap.operationID = action.payload;
    },
    setTechBundleOperationID(state, action: PayloadAction<string | null>) {
      state.techBundle.operationID = action.payload;
    },

    setTunnelDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.tunnels.open = action.payload;
    },
    setTunnelEditing: (state, action: PayloadAction<boolean>) => {
      state.tunnels.isEditing = action.payload;
    },
    setSelectedTunnel: (state, action: PayloadAction<string | null>) => {
      state.tunnels.selectedTunnelId = action.payload;
    },
    resetTunnelState: (state) => {
      tunnelsAdapter.removeAll(state.tunnels.tunnelsData);
      state.tunnels.selectedTunnelId = null;
    },
    applySocketPatchesEdges: createSocketPatchReducer(edgesAdapter, (s) => s.edgesData),
    applySocketPatchesEdgePools: createSocketPatchReducer(edgePoolsAdapter, (s) => s.edgePoolsData),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEdges.fulfilled, (state, action) => {
        edgesAdapter.setAll(state.edgesData, action.payload);
        clearStaleSelection(state);
        state.loading = false;
      })
      .addCase(fetchEdges.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchEdgePools.fulfilled, (state, action) => {
        edgePoolsAdapter.setAll(state.edgePoolsData, action.payload);
        state.edgePoolsLoading = false;
      })
      .addCase(fetchEdgePools.rejected, (state) => {
        state.edgePoolsLoading = false;
      })

      .addCase(fetchEdgeMetrics.pending, (state, action) => {
        if (!action.meta.arg.compare && action.meta.arg._showLoading) {
          state.metricsLoading = true;
        }
      })
      .addCase(fetchEdgeMetrics.fulfilled, (state, action) => {
        const { metrics, compare } = action.payload;
        if (compare) {
          Object.assign(state.compareMetrics, metrics);
          return;
        }
        Object.assign(state.metrics, metrics);
        state.metricsError = null;
        state.metricsLoading = false;
      })
      .addCase(fetchEdgeMetrics.rejected, (state, action) => {
        if (action.meta.arg.compare) return;
        state.metricsLoading = false;
        state.metricsError = action.payload as string;
      })
      .addCase(fetchEdgeMetricsAggregate.pending, (state) => {
        state.metricsLoading = true;
      })
      .addCase(fetchEdgeMetricsAggregate.fulfilled, (state, action) => {
        const data = action.payload;
        const startAt = action.meta.arg.interval.startTime;
        const endAt = action.meta.arg.interval.endTime;
        const resolution = action.meta.arg.resolution;

        for (const obj of data) {
          if (!obj.metricData.length) {
            state.aggregatedMetrics[obj.metric] = [];
            continue;
          }
          try {
            state.aggregatedMetrics[obj.metric] = fillTimeSeriesData({
              data: obj.metricData,
              startTime: startAt,
              endTime: endAt,
              interval: resolution,
              fillType: "previous",
            });
          } catch (e) {
            console.error("Error in fillTimeSeries", e, obj.metric, obj.metricData);
            state.aggregatedMetrics[obj.metric] = obj.metricData ?? [];
          }
        }
        state.metricsLoading = false;
      })
      .addCase(fetchEdgeMetricsAggregate.rejected, (state, action) => {
        console.error(action.error);
        state.metricsLoading = false;
        throw action.error;
      })
      .addCase(fetchEdgeUpgrades.fulfilled, (state, action) => {
        edgeUpgradesAdapter.setAll(state.upgradesData, action.payload);
      })
      .addCase(fetchEdgeUpgrades.rejected, (state, action) => {
        console.error(action.error);
        edgeUpgradesAdapter.removeAll(state.upgradesData);
      })
      .addCase(fetchTunnels.pending, (state) => {
        state.tunnels.loading = true;
      })
      .addCase(fetchTunnels.fulfilled, (state, action) => {
        state.tunnels.loading = false;
        tunnelsAdapter.setAll(state.tunnels.tunnelsData, action.payload || []);
      })
      .addCase(fetchTunnels.rejected, (state) => {
        state.tunnels.loading = false;
      })
      .addCase(fetchHasPartnerRadioManager.fulfilled, (state, action) => {
        state.hasPartnerRadioManager = action.payload;
      })
      .addCase(fetchHasPartnerRadioManager.rejected, (state) => {
        state.hasPartnerRadioManager = false;
      });
  },
});

export const edgeActions = edgeSlice.actions;
export default edgeSlice.reducer;

const edgeEntitySelectors = edgesAdapter.getSelectors((state: RootState) => state.edge.edgesData);
const edgePoolEntitySelectors = edgePoolsAdapter.getSelectors((state: RootState) => state.edge.edgePoolsData);
const edgeUpgradeEntitySelectors = edgeUpgradesAdapter.getSelectors(
  (state: RootState) => state.edge.upgradesData,
);
const tunnelEntitySelectors = tunnelsAdapter.getSelectors((state: RootState) => state.edge.tunnels.tunnelsData);

export const edgeData = edgeEntitySelectors.selectAll;
export const edgeEntities = edgeEntitySelectors.selectEntities;
export const edgesCount = edgeEntitySelectors.selectTotal;
export const edgePools = edgePoolEntitySelectors.selectAll;
export const edgePoolEntities = edgePoolEntitySelectors.selectEntities;
export const edgeUpgrades = edgeUpgradeEntitySelectors.selectAll;
export const edgeUpgradeEntities = edgeUpgradeEntitySelectors.selectEntities;

export const STANDALONE_EDGES_POOL_ID = "<standalone-edges>";
export const STANDALONE_EDGES_POOL_NAME = "Standalone Edges";

export function isStandaloneEdgePoolId(poolId?: string | null) {
  return !poolId || poolId === STANDALONE_EDGES_POOL_ID;
}

function emptyPoolEntityId(poolId: string | undefined) {
  return `empty-pool:${poolId ?? "unknown"}`;
}

/** Join edges to pools, attach goingOutOfSupport, build entity map - memoized once.
 * View identity is reused when every element is unchanged, so flushes that
 * touch nothing relevant are no-ops for subscribers (Layout reads this on
 * every route). */
const selectEdgesWithPoolView = createSelector(
  [edgeData, edgePools, edgeUpgrades],
  (edges, pools, upgrades) => {
    const list = buildEdgesWithPool(edges, pools, upgrades);
    const entities: Record<string, EdgeWithPool | undefined> = {};
    for (const edge of list) {
      entities[edge.id ?? emptyPoolEntityId(edge.edgePoolId)] = edge;
    }
    return { list, entities };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: (a: { list: EdgeWithPool[] }, b: { list: EdgeWithPool[] }) =>
        a.list.length === b.list.length && a.list.every((edge, i) => edge === b.list[i]),
    },
  },
);

export const edgeEdgesWithPool = createSelector([selectEdgesWithPoolView], (view) => view.list);
/**
 * Only the edges flagged as going out of support. Identity is reused while that
 * subset is unchanged, so the app shell (Layout + the TopBar chip) stops
 * re-rendering on every websocket flush that touches any edge (MCS-4316).
 */
export const edgeGoingOutOfSupport = createSelector(
  [edgeEdgesWithPool],
  (edges) => edges.filter((edge) => edge.goingOutOfSupport === true),
  {
    memoizeOptions: {
      resultEqualityCheck: (a: EdgeWithPool[], b: EdgeWithPool[]) =>
        a.length === b.length && a.every((edge, i) => edge === b[i]),
    },
  },
);

export const edgeEdgesWithPoolEntities = createSelector(
  [selectEdgesWithPoolView],
  (view) => view.entities,
);

/** AI-RAN edges with pool enrichment — shared by Radios AI RAN view and Edges filtering. */
export const aiRanEdges = createSelector([edgeEdgesWithPool], (edges) =>
  edges.filter((edge) => isAiRanEdge(edge)),
);

export const hasAiRanEdges = createSelector([aiRanEdges], (edges) => edges.length > 0);

export const nonAiRanEdges = createSelector([edgeEdgesWithPool], (edges) =>
  edges.filter((edge) => !isAiRanEdge(edge)),
);

/** True when consecutive edges disagree on networkMode (mixed-mode fleet). */
export const edgeAlarmTrigger = createSelector([edgeData], (edges) =>
  edges.some((edge, i) => i > 0 && edges[i - 1].networkMode !== edge.networkMode),
);

export const edgeState = createSelector(
  [(state: RootState) => state.edge.selectedId, edgeEdgesWithPoolEntities],
  (selectedId, entities): EdgeWithPool | null =>
    selectedId ? entities[selectedId] ?? null : null,
);

export const edgeCurrentPool = createSelector(
  [(state: RootState) => state.edge.selectedPoolId, edgePoolEntities],
  (selectedPoolId, entities): EdgePool | null =>
    selectedPoolId ? entities[selectedPoolId] ?? null : null,
);

export const selectEdgesUsingDnn = (dnnId: string | undefined) =>
  createSelector([edgeData], (edges) =>
    edges
      .filter((edge) =>
        edge.fabrics?.fabricInterfaces?.some((fabric) =>
          fabric.networks?.some((network) => network.dnnId === dnnId),
        ),
      )
      .map((edge) => edge.name),
  );

export const edgeLoading = (state: RootState) => state.edge.loading;
export const edgePoolsLoading = (state: RootState) => state.edge.edgePoolsLoading;

export const edgeSupportDisableDialogOpen = (state: RootState) =>
  state.edge.support.dialog && state.edge.support.type === "disable";
export const edgeSupportEnableDialogOpen = (state: RootState) =>
  state.edge.support.dialog && state.edge.support.type === "enable";
export const edgeSupportId = (state: RootState) => state.edge.support.id;

export const edgeMetricLoading = (state: RootState) => state.edge.metricsLoading;
export const edgeMetricsError = (state: RootState) => state.edge.metricsError;
export const edgeMetrics = (state: RootState) => state.edge.metrics;
export const edgeMetric = (metric: string) => (state: RootState) => state.edge.metrics[metric];
export const edgeCompareMetrics = (state: RootState) => state.edge.compareMetrics;
export const edgeKPIOption = (state: RootState) => state.edge.kpiOptions;
export const edgeAggregatedMetric = (metric: string) => (state: RootState) =>
  state.edge.aggregatedMetrics[metric];

export const has5gEdge = createSelector([edgeData], (edges) =>
  edges.some((edge) => edge.networkType === "5G"),
);
export const selectHasPartnerRadioManager = (state: RootState) => state.edge.hasPartnerRadioManager;

export const tunnelData = tunnelEntitySelectors.selectAll;
const tunnelEntities = tunnelEntitySelectors.selectEntities;
export const tunnelState = createSelector(
  [(state: RootState) => state.edge.tunnels.selectedTunnelId, tunnelEntities],
  (selectedTunnelId, entities): Tunnel | null =>
    selectedTunnelId ? entities[selectedTunnelId] ?? null : null,
);
export const tunnelLoading = (state: RootState) => state.edge.tunnels.loading;
export const tunnelOpen = (state: RootState) => state.edge.tunnels.open;
export const tunnelIsEditing = (state: RootState) => state.edge.tunnels.isEditing;

// Per-edge cache keyed on the raw entity: unchanged edges keep their joined
// identity across flushes, so downstream memo/React.memo boundaries hold
// (same pattern as the devices enrichment cache)
type EdgePoolJoinEntry = { sig: string; result: EdgeWithPool };
const edgeWithPoolCache = new WeakMap<Edge, EdgePoolJoinEntry>();
// Placeholder rows for empty pools, stable per pool name (bounded by pool count)
const emptyPoolRowCache = new Map<string, EdgeWithPool>();

function buildEdgesWithPool(
  edges: Edge[],
  pools: EdgePool[],
  upgrades: EdgeUpgrade[],
): EdgeWithPool[] {
  const poolByEdgeId = new Map<string, { pool: EdgePool; role?: string }>();
  const occupiedPoolIds = new Set<string>();

  for (const pool of pools) {
    for (const member of pool.edgeList) {
      poolByEdgeId.set(member.id, { pool, role: member.role });
    }
  }

  const goingOutOfSupportByEdgeId = new Map(
    upgrades
      .filter((upgrade) => typeof upgrade.goingOutOfSupport === "boolean")
      .map((upgrade) => [upgrade.id, upgrade.goingOutOfSupport as boolean]),
  );

  const result: EdgeWithPool[] = edges.map((edge) => {
    const membership = edge.id ? poolByEdgeId.get(edge.id) : undefined;
    const goingOutOfSupport = edge.id ? goingOutOfSupportByEdgeId.get(edge.id) : undefined;

    if (membership) {
      occupiedPoolIds.add(membership.pool.id);
    }

    // Signature of every joined field; reuse the previous object when the raw
    // edge and its join outcome are both unchanged
    const sig = membership
      ? `${membership.pool.edgeGroupId}|${membership.pool.name}|${membership.pool.id}|${membership.role}|${goingOutOfSupport}`
      : `standalone|${goingOutOfSupport}`;
    const cached = edgeWithPoolCache.get(edge);
    if (cached && cached.sig === sig) {
      return cached.result;
    }

    const supportFields = goingOutOfSupport !== undefined ? { goingOutOfSupport } : {};
    const joined: EdgeWithPool = membership
      ? {
        ...edge,
        edgeGroupId: membership.pool.edgeGroupId,
        edgePoolname: membership.pool.name,
        edgePoolId: membership.pool.id,
        edgeRole: membership.role,
        ...supportFields,
      }
      : {
        ...edge,
        edgePoolname: STANDALONE_EDGES_POOL_NAME,
        edgePoolId: STANDALONE_EDGES_POOL_ID,
        edgeRole: "Standalone",
        ...supportFields,
      };
    edgeWithPoolCache.set(edge, { sig, result: joined });
    return joined;
  });

  for (const pool of pools) {
    if (!occupiedPoolIds.has(pool.id)) {
      let row = emptyPoolRowCache.get(pool.id);
      if (!row) {
        row = { edgePoolname: pool.name, edgePoolId: pool.id, isEmptyPool: true };
        emptyPoolRowCache.set(pool.id, row);
      }
      result.push(row);
    }
  }

  return result;
}

setTimeout(() => {
  observeStoreChange(selectCompareEnable, (enabled) => {
    if (!enabled) store.dispatch(edgeActions.clearCompareMetrics());
  });

  edgeMetricWorker.eventEmitter().on("worker:data", (_id, _workerID, metrics) => {
    store.dispatch({
      type: fetchEdgeMetrics.fulfilled.type,
      payload: { metrics, compare: false },
    });
  });
  edgeMetricCompareWorker.eventEmitter().on("worker:data", (_id, _workerID, metrics) => {
    store.dispatch({
      type: fetchEdgeMetrics.fulfilled.type,
      payload: { metrics, compare: true },
    });
  });
});
